import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ageMinutes, type AtisScreen, type AtisScreenBroadcast } from '@jupi/shared'
import { formatClockTime, formatTime } from '@jupi/ui'

// Renders every minute and reaches the page up to 4 minutes later (API cache
// + poll interval), so 7 minutes means the display has frozen.
const FROZEN_AFTER_MIN = 7
// Top-left region with the letters, QNH and ATIS text; legible on a phone.
const CROP = { width: 0.6, height: 0.42 }

export function AtisScreens({ broadcast, now }: { broadcast: AtisScreenBroadcast; now: number }) {
  const { t } = useTranslation()
  return (
    <Stack spacing={2.5}>
      {broadcast.stale ? (
        <Chip
          size="small"
          color="warning"
          icon={<Icon icon="mdi:alert-outline" />}
          label={broadcast.staleSince ? t('weather.staleSince', { time: formatTime(broadcast.staleSince) }) : t('weather.stale')}
          sx={{ alignSelf: 'flex-start' }}
        />
      ) : null}
      {broadcast.screens.map((screen) => (
        <Screen key={screen.id} screen={screen} now={now} />
      ))}
      <Typography variant="caption" color="text.secondary">
        {t('atis.screenNote')}
      </Typography>
    </Stack>
  )
}

function Screen({ screen, now }: { screen: AtisScreen; now: number }) {
  const { t } = useTranslation()
  const [full, setFull] = useState(false)
  const age = ageMinutes(screen.capturedAt, now)
  const frozen = age > FROZEN_AFTER_MIN
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {t(`atis.screen.${screen.id}`)}
        </Typography>
        <Typography variant="body2" color={frozen ? 'warning.main' : 'text.secondary'}>
          {formatClockTime(screen.capturedAt, 'UTC')} UTC · {t('weather.minutesAgo', { count: age })}
        </Typography>
        {frozen ? <Chip size="small" color="warning" icon={<Icon icon="mdi:alert-outline" />} label={t('atis.frozen')} /> : null}
      </Stack>
      <Box
        component="button"
        type="button"
        onClick={() => setFull((f) => !f)}
        aria-label={t(full ? 'atis.showCropped' : 'atis.showFull')}
        sx={{
          p: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'zoom-in',
          bgcolor: '#fff',
          display: 'block',
          width: '100%',
          aspectRatio: full ? '1280 / 803' : `${1280 * CROP.width} / ${803 * CROP.height}`,
          position: 'relative',
          opacity: frozen ? 0.6 : 1,
        }}
      >
        <Box
          component="img"
          src={screen.imagePath}
          alt={t('atis.screenAlt', { screen: t(`atis.screen.${screen.id}`) })}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: full ? '100%' : `${100 / CROP.width}%`,
            maxWidth: 'none',
          }}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" onClick={() => setFull((f) => !f)} startIcon={<Icon icon={full ? 'mdi:crop' : 'mdi:fullscreen'} />}>
          {t(full ? 'atis.showCropped' : 'atis.showFull')}
        </Button>
        <Button size="small" href={screen.imagePath} target="_blank" rel="noreferrer" startIcon={<Icon icon="mdi:open-in-new" />}>
          {t('atis.openImage')}
        </Button>
      </Stack>
    </Stack>
  )
}
